import React, { useState, useEffect, useRef } from "react";
import "../../assets/styles/pages/admin/postbacks.css";
import AdminSideNav from "../../components/layout/adminSideNav";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import Icon from "../../components/ui/icon";
import moment from "moment-timezone";
import { getTimezoneByCountry } from "../../utils/getTimezoneByCountry";

const AdminManagePostbacksPage = () => {
    const [isSideNavActive, setisSideNavActive] = useState(false);
    const [userInfo, setUserInfo] = useState(
        JSON.parse(localStorage.getItem("AdminInfo")) || {}
    );
    const country = userInfo?.country?.toLowerCase() || "india";
    const [userVerified, setUserVerified] = useState(false);

    const [postbacks, setPostbacks] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [fetchStatus, setFetchStatus] = useState("fetching_first");
    const [skip, setSkip] = useState(0);
    const [hasMore, sethasMore] = useState(true);
    const limit = 20;
    const [isApproving, setisApproving] = useState(null);
    const [isRejecting, setisRejecting] = useState(null);
    const isLoadingMore = useRef(false);

    const showToast = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem("aToken");
        if (!token) {
            navigate("/auth/login");
            return;
        }

        const fetchUser = async () => {
            if (userVerified) return;
            try {
                const res = await api.get("/api/admin/info");
                setUserInfo(res.data.data);
                setUserVerified(true);
                localStorage.setItem("AdminInfo", JSON.stringify(res.data.data));
                fetchPostbacks();
            } catch {
                navigate("/auth/login");
            }
        };

        const fetchPostbacks = async () => {
            if (fetchStatus === "fetching") return;
            setFetchStatus("fetching");

            try {
                const res = await api.get(
                    `/api/a/postback?limit=${limit}&skip=${skip}${statusFilter !== "all" ? `&status=${statusFilter}` : ""
                    }`
                );
                setPostbacks((prev) => [...prev, ...res.data.data]);
                setSkip((prev) => prev + limit);
                setFetchStatus("fetched");
                isLoadingMore.current = false;
                if (res.data.data.length < limit) {
                    sethasMore(false);
                    window.removeEventListener("scroll", handleScroll);
                }
            } catch (err) {
                showToast({
                    status: "error",
                    message:
                        err?.response?.data?.message || "Error fetching postbacks data",
                });
            }
        };

        const handleScroll = () => {
            const scrollPosition = window.innerHeight + window.scrollY;
            const threshold = document.body.offsetHeight - 150;

            if (scrollPosition >= threshold && hasMore) {
                if (fetchStatus === "fetching") return;
                if (fetchStatus === "fetching_first") return;
                if (isLoadingMore.current) return;
                isLoadingMore.current = true;
                fetchPostbacks();
            }
        };

        fetchUser();
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [navigate, showToast, fetchStatus, skip, statusFilter, userVerified, hasMore]);

    const handleStatusChange = async (id, newStatus) => {
        if (isApproving || isRejecting) return;
        if (newStatus === "active") setisApproving(id);
        if (newStatus === "rejected") setisRejecting(id);

        try {
            await api.patch(`/api/a/postback/${id}/${newStatus}`);
            setPostbacks((prev) =>
                prev.map((p) =>
                    p._id === id ? { ...p, status: newStatus } : p
                )
            );
            showToast({
                status: "success",
                message: `Postback ${newStatus === "active" ? "approved" : "rejected"} successfully`,
            });
        } catch {
            showToast({
                status: "error",
                message: "Error updating postback status",
            });
        } finally {
            setisApproving(null);
            setisRejecting(null);
        }
    };

    const changeFilter = async (status) => {
        if (status === statusFilter) return;
        setStatusFilter(status);
        setPostbacks([]);
        setSkip(0);
        setFetchStatus("fetching");

        sethasMore(true);

        try {
            const res = await api.get(
                `/api/a/postback?limit=${limit}&skip=0${status !== "all" ? `&status=${status}` : ""
                }`
            );
            setPostbacks(res.data.data);
            setSkip(limit);
            setFetchStatus("fetched");
            if (res.data.data.length < limit) sethasMore(false);
        } catch (err) {
            showToast({
                status: "error",
                message:
                    err?.response?.data?.message || "Error fetching postbacks data",
            });
        }
    };

    return (
        <div className="apostback__page">
            <AdminSideNav
                userName={userInfo.name}
                emailID={userInfo.email}
                active={isSideNavActive}
            />

            <div
                onClick={() => setisSideNavActive(!isSideNavActive)}
                className={isSideNavActive ? "background-shadow active" : "background-shadow"}
            ></div>

            <main>
                <header className="desktop-header">
                    <div>
                        <h2>Postbacks</h2>
                        <p>Manage and review postbacks submitted by publishers.</p>
                    </div>
                </header>

                <header className="mobile-header">
                    <div><h2>Postbacks</h2></div>
                    <button className="nav-btn" onClick={() => setisSideNavActive(!isSideNavActive)}>
                        {isSideNavActive ? <Icon name="XMark" /> : <Icon name="Bars3" />}
                    </button>
                </header>

                <div className="postback-filters">
                    {["all", "active", "pending", "rejected"].map((status) => (
                        <div
                            key={status}
                            className={`status-filter ${statusFilter === status ? "active" : ""
                                }`}
                            onClick={() => changeFilter(status)}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </div>
                    ))}
                </div>

                <div className="postback-container">
                    {postbacks.length > 0 ? (
                        postbacks.map((pb) => (
                            <div key={pb._id} className={`postback-card ${pb.status}`}>
                                <div className="postback-info">
                                    <h4>{pb.campaignId?.name || "Unnamed Campaign"}</h4>
                                    <p className="publisher">Publisher: {pb.publisherId?.name}</p>
                                    <p className="status">
                                        Status:{" "}
                                        <span className={`status-tag ${pb.status}`}>
                                            {pb.status}
                                        </span>
                                    </p>
                                    <p className="time">
                                        Created:{" "}
                                        {moment(pb.createdAt).tz(getTimezoneByCountry(country)).format("DD MMM, h:mm A")}
                                    </p>
                                </div>
                                <div className="postback-actions">
                                    <div className="url-hover">
                                        <span className="url-text">View URL</span>
                                        <div className="url-popup">{pb.URL}</div>
                                    </div>
                                    <div className="btn-group">
                                        {pb.status !== "active" && (
                                            <button
                                                className="approve-btn"
                                                onClick={() => handleStatusChange(pb._id, "active")}
                                            >
                                                {isApproving === pb._id ? "Approving..." : "Approve"}
                                                {isApproving === pb._id && <Icon name="ArrowPath" className="spin" />}
                                            </button>
                                        )}
                                        {pb.status !== "rejected" && (
                                            <button
                                                className="reject-btn"
                                                onClick={() => handleStatusChange(pb._id, "rejected")}
                                            >
                                                {isRejecting === pb._id ? "Rejecting..." : "Reject"}
                                                {isRejecting === pb._id && <Icon name="ArrowPath" className="spin" />}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : fetchStatus !== "fetching_first" && fetchStatus !== "fetching" ? (
                        <div className="no-data">No postbacks found</div>
                    ) : ""}
                    {fetchStatus === "fetching_first" || fetchStatus === "fetching" ? (
                        [...Array(5)].map((_, i) => (
                            <div className="postback-card loading" key={i}>
                                <div className="skeleton"></div>
                                <div className="skeleton small"></div>
                            </div>
                        ))
                    ) : ""}
                </div>

                <span className="copyright-text">© 2025 Swift Digital Ads, All rights reserved</span>
            </main>
        </div>
    );
};

export default AdminManagePostbacksPage;